import React from 'react';
import { X, RefreshCw, RotateCcw } from 'lucide-react';
import type { DeviceType } from './types';
import { DEVICE_CONFIGS } from './deviceConfigs';

interface PreviewHeaderProps {
  frameWidth: number;
  frameHeight: number;
  scale: number;
  selectedDevice: DeviceType;
  isRotated: boolean;
  isLoading: boolean;
  onDeviceChange: (device: DeviceType) => void;
  onRotate: () => void;
  onRefresh: () => void;
  onClose: () => void;
}

export const PreviewHeader: React.FC<PreviewHeaderProps> = ({
  frameWidth,
  frameHeight,
  scale,
  selectedDevice,
  isRotated,
  isLoading,
  onDeviceChange,
  onRotate,
  onRefresh,
  onClose,
}) => (
  <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
    <div className="flex items-center space-x-4">
      <h3 className="text-sm font-semibold text-white">Preview</h3>
      <span className="text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded-full">
        {frameWidth} × {frameHeight}
        {scale < 1 && <span className="ml-1 text-gray-500">({Math.round(scale * 100)}%)</span>}
      </span>
    </div>

    {/* Device Selector */}
    <div className="flex items-center space-x-1 bg-gray-700 rounded-lg p-1">
      {(Object.keys(DEVICE_CONFIGS) as DeviceType[]).map(device => {
        const config = DEVICE_CONFIGS[device];
        const isSelected = selectedDevice === device;
        return (
          <button
            key={device}
            onClick={() => onDeviceChange(device)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              isSelected
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-600'
            }`}
            title={`${config.name} (${config.width}×${config.height})`}
          >
            {config.icon}
            <span className="hidden sm:inline">{config.name}</span>
          </button>
        );
      })}
    </div>

    {/* Action Buttons */}
    <div className="flex items-center space-x-2">
      {/* Rotate (only for mobile/tablet) */}
      {selectedDevice !== 'desktop' && (
        <button
          onClick={onRotate}
          className={`p-2 rounded-lg transition-colors ${
            isRotated
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
          title={isRotated ? 'Portrait mode' : 'Landscape mode'}
        >
          <RotateCcw size={18} className={isRotated ? 'rotate-90' : ''} />
        </button>
      )}

      {/* Refresh */}
      <button
        onClick={onRefresh}
        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
        title="Refresh preview"
      >
        <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
      </button>

      {/* Close - styled like PREVIEW MODE badge */}
      <button
        onClick={onClose}
        className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        title="Close preview (Esc)"
      >
        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
        <span className="text-sm font-medium">PREVIEW MODE</span>
        <X size={16} />
      </button>
    </div>
  </div>
);
